import { useEffect, useLayoutEffect, useRef, useState, forwardRef, useCallback } from "react";
import { useStateValue } from "../context/state/provider";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Message from "../components/Message";
import MessageLoader from "../components/MessageLoader";
import MessageInput from "../components/MessageInput";
import StreamingMessageBubble from "../components/StreamingMessageBubble";
import Loader from "react-loader-spinner";
import ErrorIcon from "@material-ui/icons/Error";
import { useDocumentData } from "react-firebase-hooks/firestore";
import LockIcon from "@material-ui/icons/Lock";
import Button from "@material-ui/core/Button";
import { VIEW } from "../constants/view.constants";

const useStyles = makeStyles({
	panelRoot: {
		display: "flex",
		flexDirection: "column",
		flex: "1 1 auto",
		minHeight: 0,
		height: "100%",
	},
	chatDisplay: {
		overflowY: "auto",
		overflowX: "hidden",
		animation: "fadeIn 0.2s ease",
		flex: "1 1 auto",
		minHeight: 0,
		padding: "18px 14px 12px",
		background:
			"radial-gradient(ellipse 80% 40% at 50% 0%, rgba(11,79,156,0.04), transparent 70%)",
		"&:focus": {
			outline: "2px solid var(--active-brand-color)",
			outlineOffset: "-2px",
		}
	},
	loginRequired: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: "12px",
		margin: "auto 20px",
		padding: "28px 24px",
		borderRadius: "16px",
		backgroundColor: "#fff",
		border: "1px solid var(--surface-border)",
		boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
		textAlign: "center",
		fontFamily: "var(--font-sans)",
	},
	loginRequiredIcon: {
		color: "var(--active-brand-color)",
		fontSize: "36px",
	},
	loginRequiredTitle: {
		fontWeight: 700,
		fontSize: "16px",
		letterSpacing: "-0.02em",
		color: "var(--color-dark-grey)",
	},
	loginRequiredText: {
		fontSize: "13.5px",
		color: "var(--color-muted)",
		lineHeight: 1.5,
	},
	loginRequiredBtn: {
		marginTop: "4px",
		backgroundColor: "var(--active-brand-color)",
		color: "#fff",
		borderRadius: "12px",
		padding: "10px 22px",
		fontSize: "13px",
		fontWeight: 600,
		fontFamily: "var(--font-sans)",
		textTransform: "none",
		"&:hover": {
			backgroundColor: "var(--active-brand-color)",
			opacity: 0.9,
		},
	},
	loadingContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: "16px",
		minHeight: "220px",
		width: "100%",
		boxSizing: "border-box",
		padding: "32px 16px",
		color: "var(--color-muted)",
		fontSize: "14px",
		fontWeight: 500,
		fontFamily: "var(--font-sans)",
		letterSpacing: "-0.01em",
		textAlign: "center",
		animation: "fadeIn 0.2s ease",
	},
	errorBox: {
		borderRadius: "10px",
		padding: "15px",
		backgroundColor: "var(--bot-msg-bg)",
		marginLeft: "42px",
	},
	optimisticUser: {
		width: "100%",
		fontSize: "1em",
		textAlign: "right",
		marginBottom: "6px",
	},
	optimisticUserBody: {
		padding: "12px 16px",
		backgroundColor: "var(--active-msg-bg)",
		color: "var(--user-msg-txt)",
		display: "inline-block",
		borderRadius: "18px 18px 6px 18px",
		marginRight: "4px",
		marginLeft: "56px",
		textAlign: "left",
		overflowWrap: "break-word",
		maxWidth: "min(78%, 540px)",
		lineHeight: 1.5,
		fontFamily: "var(--font-sans)",
		boxShadow: "0 8px 18px rgba(11, 79, 156, 0.18)",
	},
	composerWrap: {
		position: "relative",
		flexShrink: 0,
	},
	scrollBottomBtn: {
		position: "absolute",
		right: 18,
		top: -44,
		zIndex: 4,
		border: "1px solid var(--surface-border)",
		background: "#fff",
		color: "var(--color-dark-grey)",
		boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
		borderRadius: 999,
		padding: "8px 12px",
		fontFamily: "var(--font-sans)",
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		"&:hover": {
			background: "var(--color-alt-grey)",
		},
		"&:focus-visible": {
			outline: "2px solid var(--active-brand-color)",
			outlineOffset: 2,
		},
	},
	"@media (max-width: 375px)": {
		chatDisplay: {
			padding: "10px 6px 6px",
		},
	},
});

const ChatPanel = forwardRef(({
	messages,
	messagesRef,
	firestore,
	fsLoading,
	fsError,
	online,
	userLoading,
	disableBtn,
	setErrMsg,
	setShowErrorMessage,
	justOpen,
	handleJustOpen,
	chatbotIsDraft,
	user,
	userLoadingAU,
	chatbotId,
	avatarUrl,
	handleFallbackMessage,
	clearFallbackMessage,
	brandingTitle,
	isFullView = true,
}, ref) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [optimisticUserText, setOptimisticUserText] = useState(null);
	const [streamingDisplay, setStreamingDisplay] = useState("");
	const streamTargetRef = useRef("");
	const streamShownRef = useRef("");
	const streamRafRef = useRef(null);
	const streamDoneRef = useRef(false);
	const pendingBotMessageIdRef = useRef(null);
	const streamFailsafeTimerRef = useRef(null);
	const [{ loaded, admin, loggingOut, user: stateUser }, dispatch] = useStateValue();
	const messagesEndRef = useRef(null);
	const chatPanelRef = useRef(null);
	const [submittingMsg, setSubmittingMsg] = useState(false);
	const [justFlag, setJustFlag] = useState(false);
	const [requiresMSLogin, setRequiresMSLogin] = useState(false);
	const [showScrollBottom, setShowScrollBottom] = useState(false);

	const clearStreamingState = useCallback(() => {
		if (streamRafRef.current) {
			cancelAnimationFrame(streamRafRef.current);
			streamRafRef.current = null;
		}
		if (streamFailsafeTimerRef.current) {
			clearTimeout(streamFailsafeTimerRef.current);
			streamFailsafeTimerRef.current = null;
		}
		streamTargetRef.current = "";
		streamShownRef.current = "";
		streamDoneRef.current = false;
		pendingBotMessageIdRef.current = null;
		setStreamingDisplay("");
	}, []);

	const isBotTurnMessage = useCallback(
		(msg) => {
			if (!msg?.data || msg.id === "sse-streaming") return false;
			const roomUid = `${user?.uid}***${chatbotId}`;
			if (msg.data.uid === roomUid) return false;
			// Text answer and follow-up Wiki image cards (often lack chatGptResponse)
			return Boolean(
				msg.data.chatGptResponse ||
					msg.data.questionMessageId ||
					msg.data.questionForm ||
					msg.data.linkIkea ||
					msg.data.linkStore
			);
		},
		[user, chatbotId]
	);

	const scrollChatToBottom = useCallback(() => {
		const panel = chatPanelRef.current;
		if (panel) {
			// Only scroll the chat panel itself. Avoid scrollIntoView — in an
			// iframe embed it can scroll/shift the parent page (e.g. wizard).
			panel.scrollTop = panel.scrollHeight;
		}
		setShowScrollBottom(false);
	}, []);

	const handleChatScroll = useCallback(() => {
		const panel = chatPanelRef.current;
		if (!panel) return;
		const distanceFromBottom =
			panel.scrollHeight - panel.scrollTop - panel.clientHeight;
		setShowScrollBottom(distanceFromBottom > 120);
	}, []);

	const scheduleStreamFailsafeClear = useCallback(() => {
		if (streamFailsafeTimerRef.current) {
			clearTimeout(streamFailsafeTimerRef.current);
		}
		// Server writes the bot message before sending `done`; don't leave the
		// preview bubble stuck if Firestore lag or image-card ordering delays swap.
		streamFailsafeTimerRef.current = setTimeout(() => {
			streamFailsafeTimerRef.current = null;
			if (streamDoneRef.current && streamShownRef.current) {
				clearStreamingState();
			}
		}, 450);
	}, [clearStreamingState]);

	const tickStreamReveal = useCallback(() => {
		const target = streamTargetRef.current;
		const shown = streamShownRef.current;
		if (shown.length < target.length) {
			const lag = target.length - shown.length;
			// Catch up faster when far behind; otherwise ease 1–3 chars per frame
			const step = lag > 60 ? Math.ceil(lag / 6) : lag > 20 ? 3 : lag > 6 ? 2 : 1;
			streamShownRef.current = target.slice(0, shown.length + step);
			setStreamingDisplay(streamShownRef.current);
			streamRafRef.current = requestAnimationFrame(tickStreamReveal);
			return;
		}
		streamRafRef.current = null;
		if (streamDoneRef.current) {
			scheduleStreamFailsafeClear();
		}
	}, [scheduleStreamFailsafeClear]);

	const handleStreamToken = useCallback((delta) => {
		if (delta == null) return;
		streamTargetRef.current += delta;
		if (!streamRafRef.current) {
			streamRafRef.current = requestAnimationFrame(tickStreamReveal);
		}
	}, [tickStreamReveal]);

	const handleStreamClear = useCallback((doneData) => {
		if (doneData?.force) {
			clearStreamingState();
			return;
		}
		streamDoneRef.current = true;
		if (doneData?.botMessageId) {
			pendingBotMessageIdRef.current = doneData.botMessageId;
		}
		// Finish revealing any buffered chars, then failsafe-clear so the
		// preview cannot linger under/after the persisted answer + image cards.
		if (streamShownRef.current.length < streamTargetRef.current.length) {
			if (!streamRafRef.current) {
				streamRafRef.current = requestAnimationFrame(tickStreamReveal);
			}
		} else {
			scheduleStreamFailsafeClear();
		}
	}, [tickStreamReveal, scheduleStreamFailsafeClear, clearStreamingState]);

	// Swap stream bubble for the persisted bot message once it lands
	useEffect(() => {
		if (!streamDoneRef.current || !streamingDisplay || !messages?.length) return;

		const pendingId = pendingBotMessageIdRef.current;
		if (pendingId && messages.some((m) => m.id === pendingId)) {
			clearStreamingState();
			return;
		}

		// Prefer matching any recent bot turn message (text or follow-up image card)
		const recent = messages.slice(-6);
		if (recent.some(isBotTurnMessage)) {
			clearStreamingState();
		}
	}, [messages, streamingDisplay, clearStreamingState, isBotTurnMessage]);

	useEffect(() => {
		return () => {
			if (streamFailsafeTimerRef.current) {
				clearTimeout(streamFailsafeTimerRef.current);
			}
			if (streamRafRef.current) {
				cancelAnimationFrame(streamRafRef.current);
			}
		};
	}, []);

	const handleOptimisticUserMessage = useCallback((text) => {
		setOptimisticUserText(text);
		clearStreamingState();
	}, [clearStreamingState]);

	// Drop optimistic bubble once Firestore has the real user message
	useEffect(() => {
		if (!optimisticUserText || !messages?.length || !user?.uid) return;
		const roomUid = `${user.uid}***${chatbotId}`;
		const matched = messages.some((m) => {
			if (m.data?.uid !== roomUid) return false;
			const t = Array.isArray(m.data.text) ? m.data.text[0] : m.data.text;
			return t === optimisticUserText;
		});
		if (matched) setOptimisticUserText(null);
	}, [messages, optimisticUserText, user, chatbotId]);

	// Keep the viewport pinned to the bottom while the stream / optimistic bubble grows
	useLayoutEffect(() => {
		if (streamingDisplay || optimisticUserText || loading) {
			scrollChatToBottom();
		}
	}, [streamingDisplay, optimisticUserText, loading, scrollChatToBottom]);

	useEffect(() => {
		if (!chatbotId || !firestore) return;
		firestore.collection("chatbotsMSlogin").doc(chatbotId).get()
			.then(doc => setRequiresMSLogin(doc.exists))
			.catch(() => setRequiresMSLogin(false));
	}, [chatbotId, firestore]);

	const isAuthenticated = stateUser?.loggedIn === true;

	// Add these for screen reader announcements
	const [lastMessageId, setLastMessageId] = useState("");
	const [isStreaming, setIsStreaming] = useState(false);
	const [completeMessage, setCompleteMessage] = useState("");
	const announcementRef = useRef(null);
	const streamTimeoutRef = useRef(null);
	const [lastStreamUpdate, setLastStreamUpdate] = useState(0);
	const STREAM_IDLE_THRESHOLD = 2000; // 2 seconds without updates means streaming is complete


	function handleFlag() {
		setJustFlag(true)
	}

	function handleSubmit() {
		setSubmittingMsg(true)
		setJustFlag(false)
	}

	// Add this effect to announce new messages to screen readers
	// Function to announce a message immediately
	const announceMessage = useCallback((msg, prefix = "") => {
		if (!msg.data || !msg.data.text) return;

		const messageText = Array.isArray(msg.data.text)
			? msg.data.text[0].replace(/<[^>]*>/g, '')
			: msg.data.text.replace(/<[^>]*>/g, '');

		if (announcementRef.current) {
			announcementRef.current.textContent = "";
			void announcementRef.current.offsetWidth;
			announcementRef.current.textContent = prefix + messageText;
		}
	}, []);

	// Function to announce the complete message after streaming
	const announceCompleteMessage = useCallback((prefix = "") => {
		if (announcementRef.current && completeMessage) {
			announcementRef.current.textContent = "";
			void announcementRef.current.offsetWidth;
			announcementRef.current.textContent = prefix + completeMessage;

			// Reset the complete message
			setCompleteMessage("");
		}
	}, [completeMessage]);

	// Add this effect to detect new messages and announce them
	useEffect(() => {
		if (messages && messages.length > 0) {
			const lastMsg = messages[messages.length - 1];
			
			// Skip if this isn't a new message
			if (!lastMsg || lastMsg.id === lastMessageId) return;
			
			// Update the last message ID
			setLastMessageId(lastMsg.id);
			
			// Announce the message with appropriate prefix
			if (lastMsg.data.uid === user?.uid + "***" + chatbotId) {
				announceMessage(lastMsg, "You said: ");
			} else {
				const sender = lastMsg.data.uid === "Live Agent" 
					? "Live Agent says: " 
					: (lastMsg.data.chatbotName || "Agent") + " says: ";
				
				announceMessage(lastMsg, sender);
			}
		}
	}, [messages, lastMessageId, user, chatbotId, announceMessage]);

	// toggle on chatPanel render to force scroll when opening.
	useEffect(() => {
		dispatch({
			type: "CHAT_PANEL_STATE",
			loaded: true,
		});

		return () => {
			dispatch({
				type: "CHAT_PANEL_STATE",
				loaded: false,
			});
		};
	}, [dispatch]);

	const roomRef =
		!userLoadingAU && !fsError && user
			? firestore.collection("rooms").doc(user.uid + "***" + chatbotId)
			: "";

	const [roomValue] = useDocumentData(roomRef);
	// Manage message loader on pendingMessages change
	useEffect(() => {
		roomValue && roomValue.pendingMessages > 0
			? setLoading(true)
			: setLoading(false);
	}, [roomValue]);

	//to reset loader after the network disconnect
	useEffect(() => {
		if (!online) {
			setLoading(false);
		}
	}, [online]);

	useEffect(() => {
		if (!justFlag && messagesEndRef.current) {
		}
		else if (!justFlag && !loading) {
			setSubmittingMsg(false)
		}
	}, [loading, loaded, roomRef, justFlag]);

	//********************************************
	const scrollToBottom = () => {
		chatPanelRef.current.scrollTop = chatPanelRef.current.scrollHeight;
	};

	const handleImageLoad = () => {
		if (!justFlag && !loading) {
			scrollToBottom(); // Scroll to bottom only if not flagged and not loading
		}
		setSubmittingMsg(false);
	};
	//********************************************

	useEffect(() => {
		if (messages) {
		}

		if (chatPanelRef.current) {
			chatPanelRef.current.scrollTop =
				chatPanelRef.current.scrollHeight;
		}
	}, [messages, loading
	]);

	const showAvatar = (currentUid, currentIndex) => {
		const nextMsg = messages[currentIndex + 1];
		if (!nextMsg || nextMsg.data.uid === currentUid) {
			return false;
		}
		return true;
	};

	const showDate = (time, currentIndex) => {
		const nextMsg = messages[currentIndex + 1] || null;
		const currentMsgDay = time.toDate().toDateString();
		const nextMsgDay = nextMsg
			? nextMsg.data.createdAt.toDate().toDateString()
			: null;

		const dates = {};
		const today = new Date();

		if (nextMsgDay && currentMsgDay !== nextMsgDay) {
			//add next message date
			dates.nextDate = nextMsgDay;
		}
		if (currentIndex === 0 && currentMsgDay !== today.toDateString()) {
			// return prev and previous date
			dates.prevDate = currentMsgDay;
		}
		return dates;
	};

	return (
		<div className={classes.panelRoot}>
			<div
				className={classes.chatDisplay}
				ref={chatPanelRef}
				aria-label="Chat conversation"
				aria-live="polite"
				aria-atomic="false"
				aria-relevant="additions"
				role="log"
				tabIndex="0"
				onScroll={handleChatScroll}
			>
				{!userLoading &&
					messages &&
					messages.length &&
					!fsLoading &&
					!fsError ? (
					<>
						{messages.map((msg, i) => (
							<Message
								handleFlag={handleFlag}
								key={msg.id}
								messageId={msg.id}
								adminStatus={admin}
								flagged={msg.data.adminFlag}
								lastMessage={
									messages.length === i + 1 &&
									!streamingDisplay &&
									!optimisticUserText
								}
								showUser={showAvatar(msg.data.uid, i)}
								showDate={showDate(msg.data.createdAt, i)}
								options={msg.data.options}
								multipleAnswers={msg.data.multipleAnswers}
								user={user}
								uid={msg.data.uid}
								uidDefinition={msg.data.uidDefinition}
								avatar={msg.data.photoUrl}
								message={msg}
								time={msg.data.createdAt.toDate()}
								messagesRef={messagesRef}
								online={online}
								messageQty={messages.length}
								chatbotIsDraft={chatbotIsDraft}
								onImageLoad={handleImageLoad}
								chatbotId={chatbotId}
								requestCancelled={msg.data.requestCancelled}
								setLoading={setLoading}
								handleFallbackMessage={handleFallbackMessage}
								avatarUrl={avatarUrl}
							/>
						))}
						{optimisticUserText && (
							<div className={classes.optimisticUser} aria-label="User message" role="article">
								<div className={classes.optimisticUserBody}>
									{optimisticUserText}
								</div>
							</div>
						)}
						{streamingDisplay ? (
							<StreamingMessageBubble
								text={streamingDisplay}
								avatarUrl={avatarUrl}
							/>
						) : null}
						{/* Show ... only after the user bubble is visible, and before tokens arrive */}
						{loading &&
							online &&
							!streamingDisplay &&
							(optimisticUserText ||
								(messages?.length > 0 &&
									messages[messages.length - 1]?.data?.uid ===
										`${user?.uid}***${chatbotId}`)) && (
								<MessageLoader />
							)}
						{!online && (
							<Box m={2} className={classes.errorBox} role="alert">
								<Box>
									<ErrorIcon color="error" />
								</Box>
								<Typography>
									Network Error.
									<br />
									Connection required to send and receive
									messages.
								</Typography>
							</Box>
						)}
						{chatbotIsDraft && (
							<Box m={2} className={classes.errorBox} role="alert">
								<Box>
									<ErrorIcon color="error" />
								</Box>
								<Typography>
									The agent is currently in draft mode.
								</Typography>
							</Box>
						)}
						{<div ref={messagesEndRef} />}
					</>
				) : (
					<>
						{(!fsError || userLoading) && (
							<div
								className={classes.loadingContainer}
								role="status"
								aria-label="Loading messages"
							>
								<Loader
									type="Oval"
									color="var(--active-brand-color)"
									height={48}
									width={48}
									aria-hidden="true"
								/>
								<span>Loading messages…</span>
							</div>
						)}
						{fsError && !userLoading && (
							<Box
								className={classes.errorBox}
								m={7}
								display="flex"
								justifyContent="center"
								alignItems="center"
								role="alert"
							>
								<Typography variant="h7">
									<ErrorIcon color="error" />
									<br />
									An error occured. <br />
									<br /> Please try refreshing the page.{" "}
									<br />
								</Typography>
							</Box>
						)}{" "}
					</>
				)}
			</div>
			{requiresMSLogin && !isAuthenticated ? (
				<Box className={classes.loginRequired} role="status">
					<LockIcon className={classes.loginRequiredIcon} />
					<Typography className={classes.loginRequiredTitle}>
						Login required
					</Typography>
					<Typography className={classes.loginRequiredText}>
						Please sign in with your Microsoft account to start a conversation.
					</Typography>
					<Button
						className={classes.loginRequiredBtn}
						onClick={() => dispatch({ type: "SET_VIEW", view: VIEW.LOGIN })}
					>
						Sign In
					</Button>
				</Box>
			) : (
				!loggingOut && (
					<div className={classes.composerWrap}>
						{showScrollBottom && (
							<button
								type="button"
								className={classes.scrollBottomBtn}
								onClick={scrollChatToBottom}
								aria-label="Jump to latest messages"
							>
								↓ Latest
							</button>
						)}
						<MessageInput
							online={online}
							messagesRef={messagesRef}
							firestore={firestore}
							setErrMsg={setErrMsg}
							setShowErrorMessage={setShowErrorMessage}
							disableBtn={disableBtn}
							messages={messages?.length}
							justFlag={justFlag}
							handleSubmit={handleSubmit}
							justOpen={justOpen}
							handleJustOpen={handleJustOpen}
							setLoading={setLoading}
							chatbotIsDraft={chatbotIsDraft}
							chatbotId={chatbotId}
							handleFallbackMessage={handleFallbackMessage}
							clearFallbackMessage={clearFallbackMessage}
							onOptimisticUserMessage={handleOptimisticUserMessage}
							onStreamToken={handleStreamToken}
							onStreamClear={handleStreamClear}
							brandingTitle={brandingTitle}
						/>
					</div>
				)
			)}
		</div>
	);
})

export default ChatPanel;
